import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiPlus, 
  FiEdit3, 
  FiTrash2, 
  FiUsers, 
  FiEye, 
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiMapPin,
  FiPhone,
  FiMail
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './Congregacoes.css';

const Congregacoes = () => {
  const navigate = useNavigate();
  const [congregacoes, setCongregacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroAtivo, setFiltroAtivo] = useState('todos');
  const [congregacaoParaExcluir, setCongregacaoParaExcluir] = useState(null);
  const [mostrarModalExclusao, setMostrarModalExclusao] = useState(false);
  const [stats, setStats] = useState({ total: 0, ativas: 0, inativas: 0 });

  useEffect(() => {
    carregarCongregacoes();
    carregarEstatisticas();
  }, []);

  const carregarCongregacoes = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/congregacoes', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCongregacoes(data || []);
      } else {
        toast.error('Erro ao carregar congregações');
      }
    } catch (error) {
      console.error('Erro ao carregar congregações:', error);
      toast.error('Erro ao carregar congregações');
    } finally {
      setLoading(false);
    }
  };

  const carregarEstatisticas = async () => {
    try {
      const response = await fetch('/api/congregacoes/estatisticas', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const confirmarExclusao = (congregacao) => {
    setCongregacaoParaExcluir(congregacao);
    setMostrarModalExclusao(true);
  };

  const excluirCongregacao = async () => {
    if (!congregacaoParaExcluir) return;

    try {
      const response = await fetch(`/api/congregacoes/${congregacaoParaExcluir.id}/desativar`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Congregação desativada com sucesso!');
        carregarCongregacoes();
        carregarEstatisticas();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao desativar congregação');
      }
    } catch (error) {
      console.error('Erro ao desativar congregação:', error);
      toast.error('Erro ao desativar congregação');
    } finally {
      setMostrarModalExclusao(false);
      setCongregacaoParaExcluir(null);
    }
  };

  const ativarCongregacao = async (id) => {
    try {
      const response = await fetch(`/api/congregacoes/${id}/ativar`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Congregação ativada com sucesso!');
        carregarCongregacoes();
        carregarEstatisticas();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao ativar congregação');
      }
    } catch (error) {
      console.error('Erro ao ativar congregação:', error);
      toast.error('Erro ao ativar congregação');
    }
  };

  const congregacoesFiltradas = congregacoes.filter(congregacao => {
    const matchesSearch = congregacao.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (congregacao.endereco && congregacao.endereco.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = filtroAtivo === 'todos' || 
                         (filtroAtivo === 'ativas' && congregacao.ativo) ||
                         (filtroAtivo === 'inativas' && !congregacao.ativo);
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando congregações...</p>
      </div>
    );
  }

  return (
    <div className="congregacoes-container">
      <div className="page-header">
        <div className="header-content">
          <h1>
            <FiUsers className="page-icon" />
            Congregações
          </h1>
          <p className="page-description">
            Gerencie as congregações da igreja
          </p>
        </div>
        <button 
          className="btn-primary"
          onClick={() => navigate('/congregacoes/nova')}
        >
          <FiPlus /> Nova Congregação
        </button>
      </div>

      {/* Estatísticas */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon total">
            <FiUsers />
          </div>
          <div className="stat-content">
            <h3>{stats.total}</h3>
            <p>Total</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon active">
            <FiEye />
          </div>
          <div className="stat-content">
            <h3>{stats.ativas}</h3>
            <p>Ativas</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon inactive">
            <FiEye />
          </div>
          <div className="stat-content">
            <h3>{stats.inativas}</h3>
            <p>Inativas</p>
          </div>
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="filters-section">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar congregações..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filtroAtivo === 'todos' ? 'active' : ''}`}
            onClick={() => setFiltroAtivo('todos')}
          >
            <FiFilter /> Todos
          </button>
          <button 
            className={`filter-btn ${filtroAtivo === 'ativas' ? 'active' : ''}`}
            onClick={() => setFiltroAtivo('ativas')}
          >
            <FiEye /> Ativas
          </button>
          <button 
            className={`filter-btn ${filtroAtivo === 'inativas' ? 'active' : ''}`}
            onClick={() => setFiltroAtivo('inativas')}
          >
            <FiEye /> Inativas
          </button>
        </div>
        
        <button 
          className="btn-secondary"
          onClick={() => {
            carregarCongregacoes();
            carregarEstatisticas();
          }}
        >
          <FiRefreshCw /> Atualizar
        </button>
      </div>

      {/* Lista de Congregações */}
      <div className="congregacoes-grid">
        {congregacoesFiltradas.length === 0 ? (
          <div className="empty-state">
            <FiUsers className="empty-icon" />
            <h3>Nenhuma congregação encontrada</h3>
            <p>Não há congregações que correspondam aos filtros aplicados.</p>
            <button 
              className="btn-primary"
              onClick={() => navigate('/congregacoes/nova')}
            >
              <FiPlus /> Adicionar Primeira Congregação
            </button>
          </div>
        ) : (
          congregacoesFiltradas.map(congregacao => (
            <div key={congregacao.id} className={`congregacao-card ${!congregacao.ativo ? 'inactive' : ''}`}>
              <div className="card-header">
                <h3>{congregacao.nome}</h3>
                <div className="card-actions">
                  <button 
                    className="btn-icon"
                    onClick={() => navigate(`/congregacoes/editar/${congregacao.id}`)}
                    title="Editar"
                  >
                    <FiEdit3 />
                  </button>
                  {congregacao.ativo ? (
                    <button 
                      className="btn-icon danger"
                      onClick={() => confirmarExclusao(congregacao)}
                      title="Desativar"
                    >
                      <FiTrash2 />
                    </button>
                  ) : (
                    <button 
                      className="btn-icon success"
                      onClick={() => ativarCongregacao(congregacao.id)}
                      title="Ativar"
                    >
                      <FiEye />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="card-content">
                {congregacao.descricao && (
                  <p className="congregacao-descricao">{congregacao.descricao}</p>
                )}
                
                <div className="congregacao-info">
                  {congregacao.endereco && (
                    <div className="info-item">
                      <FiMapPin className="info-icon" />
                      <span>{congregacao.endereco}</span>
                    </div>
                  )}
                  
                  {congregacao.telefone && (
                    <div className="info-item">
                      <FiPhone className="info-icon" />
                      <span>{congregacao.telefone}</span>
                    </div>
                  )}
                  
                  {congregacao.email && (
                    <div className="info-item">
                      <FiMail className="info-icon" />
                      <span>{congregacao.email}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="card-footer">
                <span className={`status-badge ${congregacao.ativo ? 'active' : 'inactive'}`}>
                  {congregacao.ativo ? 'Ativa' : 'Inativa'}
                </span>
                <span className="created-date">
                  Criada em {new Date(congregacao.created_at).toLocaleDateString('pt-BR')}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de Confirmação de Exclusão */}
      {mostrarModalExclusao && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirmar Desativação</h3>
            <p>
              Tem certeza que deseja desativar a congregação <strong>{congregacaoParaExcluir?.nome}</strong>?
            </p>
            <p className="warning-text">
              Esta ação pode ser revertida posteriormente.
            </p>
            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => {
                  setMostrarModalExclusao(false);
                  setCongregacaoParaExcluir(null);
                }}
              >
                Cancelar
              </button>
              <button 
                className="btn-danger"
                onClick={excluirCongregacao}
              >
                Desativar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Congregacoes;